import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const prefersReducedMotion =
	typeof window !== "undefined" &&
	window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/**
 * Reading progress bar at top of page.
 * Fills left -> right as the user scrolls.
 */
function initReadingProgress(): void {
	const bar = document.querySelector<HTMLSpanElement>(".progress__bar");
	if (!bar) return;

	const update = (): void => {
		const doc = document.documentElement;
		const scrollTop = window.scrollY || doc.scrollTop;
		const max = doc.scrollHeight - window.innerHeight;
		const pct = max > 0 ? Math.min(1, Math.max(0, scrollTop / max)) : 0;
		bar.style.width = `${(pct * 100).toFixed(2)}%`;
	};

	update();
	window.addEventListener("scroll", update, { passive: true });
	window.addEventListener("resize", update, { passive: true });
}

/**
 * Pull-quotes: hairlines draw left -> right first, then italic line fades in.
 * If reduce-motion, leave them visible immediately.
 */
function initPullQuotes(): void {
	const quotes = document.querySelectorAll<HTMLElement>(".pq");
	if (quotes.length === 0) return;

	for (const quote of quotes) {
		if (prefersReducedMotion) {
			quote.classList.add("is-revealed");
			continue;
		}
		quote.classList.add("js-enabled");

		ScrollTrigger.create({
			trigger: quote,
			start: "top 85%",
			once: true,
			onEnter: () => {
				quote.classList.add("is-revealed");
			},
		});
	}
}

/**
 * Side-note slides in from the left when entering viewport.
 */
function initSideNote(): void {
	const note = document.querySelector<HTMLElement>(".sidenote");
	if (!note) return;

	if (prefersReducedMotion) {
		note.classList.add("is-revealed");
		return;
	}
	note.classList.add("js-enabled");

	ScrollTrigger.create({
		trigger: note,
		start: "top 85%",
		once: true,
		onEnter: () => {
			note.classList.add("is-revealed");
		},
	});
}

/**
 * CTA press-down on click is handled via CSS :active.
 * Hook here for any extra JS-only details (e.g. analytics).
 */
function initCta(): void {
	const cta = document.querySelector<HTMLAnchorElement>(".cta");
	if (!cta) return;
	cta.addEventListener("click", () => {
		// hook for telemetry later
	});
}

export function initMotion(): void {
	initReadingProgress();
	initPullQuotes();
	initSideNote();
	initCta();
}
